import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { MaskLine, easeOutExpo, scrollToEl } from "../lib/anim";

export const Hero = ({ onDownload }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -6]);
  const glowScale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);

  const scrollTo = (id) => scrollToEl(id);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-16 overflow-hidden"
    >
      {/* Background grid + radial glows */}
      <div className="absolute inset-0 gx-grid-bg opacity-60" />
      <div className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#00B4D8]/10 blur-[120px]" />
      <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-[#4CAF50]/10 blur-[130px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center w-full">
        {/* Left copy */}
        <div>
          <h1
            data-testid="hero-headline"
            className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05]"
          >
            <MaskLine delay={0.1} mode="load">Map your streets.</MaskLine>
            <MaskLine delay={0.25} mode="load" className="gx-text-gradient">
              Master your
            </MaskLine>
            <MaskLine delay={0.4} mode="load" className="gx-text-gradient">
              fuel budget.
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: easeOutExpo }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            Map Indian roads, monetize your idle internet, and earn up to{" "}
            <span className="text-[#4CAF50] font-semibold">&#8377;11,700+</span>{" "}
            monthly. 100% paid directly in bank-compliant Indian Rupees (INR).
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: easeOutExpo }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-download-btn"
              onClick={onDownload}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4CAF50] px-7 py-4 text-[15px] font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow"
            >
              <Download size={19} className="transition-transform duration-200 group-hover:translate-y-0.5" />
              Download for Android
            </button>
            <button
              data-testid="hero-how-it-works-btn"
              onClick={() => scrollTo("#how-it-works")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-4 text-[15px] font-semibold text-slate-100 hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
            >
              How It Works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-11 flex items-center gap-7"
          >
            {[
              { k: "100%", v: "Fiat INR Payouts" },
              { k: "2", v: "Earning Engines" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-mono-gx text-2xl font-bold text-white">{s.k}</div>
                <div className="text-xs text-slate-400 mt-0.5">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right phone mockup */}
        <div className="relative flex justify-center lg:justify-end">
          <motion.div
            style={{ scale: glowScale }}
            className="absolute inset-0 m-auto h-[70%] w-[70%] rounded-full bg-[#00B4D8]/25 blur-[90px]"
          />
          <motion.div
            style={{ y: phoneY, rotate: phoneRotate }}
            initial={{ opacity: 0, y: 60, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: easeOutExpo }}
            className="relative"
          >
            <motion.img
              src="/hero-phone.png"
              alt="GeoNetix live mapping and rewards on a smartphone"
              data-testid="hero-phone-image"
              className="relative w-[300px] sm:w-[360px] lg:w-[420px] drop-shadow-[0_40px_80px_rgba(0,0,0,0.55)]"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* floating tags */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="absolute top-16 -left-4 sm:-left-10 gx-glass border border-[#00B4D8]/40 rounded-2xl px-4 py-3"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#00B4D8] font-mono-gx">Mapping Layer</div>
              <div className="text-sm font-semibold">Live · Recording</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              className="absolute bottom-24 -right-2 sm:-right-8 gx-glass border border-[#4CAF50]/40 rounded-2xl px-4 py-3"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#4CAF50] font-mono-gx">Today</div>
              <div className="text-sm font-semibold">+ &#8377;312 earned</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("#how-it-works")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-slate-400 hidden md:flex flex-col items-center gap-2"
        aria-label="Scroll down"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono-gx">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
};
